// src/components/auth/CognitoLoginForm.jsx
// Cognito-based login form to replace Auth0 integration

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCognitoAuth } from "../../aws-amplify-auth";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Link,
  Tabs,
  Tab,
  CircularProgress,
  AppBar,
  Toolbar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import HeaderWhite from "../../media/shieldshot_header_white.png";
import { STORE_URL } from '../../config';

const CognitoLoginForm = () => {
  const {
    isAuthenticated,
    loading,
    profileLoading,
    signIn,
    signUp,
    confirmSignUp,
    resetPassword,
    confirmResetPassword,
    clearAuthState, // Add this for error recovery
  } = useCognitoAuth();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(0); // Start with sign-in by default
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    confirmationCode: '',
    firstName: '',
    lastName: '',
    username: '', // Add username field
    accountType: 'distributor', // Default to distributor for this frontend
  });
  const [errors, setErrors] = useState({});
  const [loading2, setLoading2] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [step, setStep] = useState('initial'); // initial, confirmSignUp, resetPassword, confirmReset
  const [signUpUsername, setSignUpUsername] = useState(''); // Store generated username for verification

  // Ref for customer-store redirect timer (SS-411) — cleaned up on unmount
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    // Clear form when switching tabs
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      confirmationCode: '',
      firstName: '',
      lastName: '',
      username: '', // Add username field
      accountType: 'distributor', // Default to distributor for this frontend
    });
    setErrors({});
    setMessage('');
    setStep('initial');
    setSignUpUsername(''); // Clear stored username
    setRedirecting(false); // Reset redirecting state
  }, [currentTab]);

  // Reset redirecting state when user becomes unauthenticated
  useEffect(() => {
    if (!isAuthenticated && !loading && !profileLoading) {
      setRedirecting(false);
    }
  }, [isAuthenticated, loading, profileLoading]);

  // Monitor URL parameters for mode changes
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'signup') {
      setCurrentTab(1);
    } else {
      setCurrentTab(0);
    }
  }, [searchParams]);

  // Effect to handle authentication state changes
  useEffect(() => {
    console.log('Auth state changed:', { isAuthenticated, loading, profileLoading, redirecting });

    // Only redirect if we're authenticated, not loading (auth or profile), and not already redirecting
    if (isAuthenticated && !loading && !profileLoading && !redirecting) {
      setRedirecting(true);

      // For users already logged in visiting /login, redirect immediately to dashboard
      // For users who just logged in, check for a stored redirect path
      const redirectPath = localStorage.getItem('redirectAfterLogin') || '/dashboard';
      localStorage.removeItem('redirectAfterLogin');
      console.log('useEffect: Authentication confirmed, redirecting to:', redirectPath);

      // Use immediate navigation for already-authenticated users
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, loading, profileLoading, redirecting, navigate]);

  // Cleanup for customer-store redirect timer (SS-411)
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  if (loading || profileLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ backgroundColor: "#2997ff" }}>
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  // Show loading if already authenticated to prevent form flash
  if (isAuthenticated || redirecting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ backgroundColor: "#2997ff" }}>
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  // Redirect logic is now handled in useEffect above

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (currentTab === 1) { // Sign up
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      if (!formData.username) {
        newErrors.username = 'Username is required';
      } else if (formData.username.length < 6) {
        newErrors.username = 'Username must be at least 6 characters';
      } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
        newErrors.username = 'Username can only contain letters, numbers, and underscores';
      }

      // firstName and lastName are now optional - no validation needed
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading2(true);
    console.log('Starting sign-in process...');
    const result = await signIn(formData.email, formData.password);
    console.log('Sign-in result:', result);

    if (result.success) {
      setMessage('Sign in successful! Redirecting...');
      setMessageType('success');

      console.log('Sign-in successful, auth state will handle navigation...');

      // Clear any error states and prepare for redirect
      setErrors({});

      // Don't manually navigate here - let the useEffect handle it to avoid race conditions
      // The useEffect will trigger when isAuthenticated becomes true
    } else {
      // SS-411: Customer/consumer accounts denied portal access → redirect to store
      if (result.redirectToStore) {
        setMessage('Customer accounts use the ShieldShot Store. Redirecting you to the store\u2026');
        setMessageType('info');
        // Clear any previous timer
        if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = setTimeout(() => {
          window.location.href = STORE_URL;
        }, 2500);
        setLoading2(false);
        return;
      }

      // Check for specific errors that require clearing auth state
      if (result.error && result.error.includes('already a signed in user')) {
        setMessage('Clearing previous session and retrying...');
        setMessageType('info');
        await clearAuthState();
        return;
      }

      setMessage(result.error || 'Sign in failed');
      setMessageType('error');
    }
    setLoading2(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading2(true);
    const result = await signUp(formData.email, formData.password, {
      given_name: formData.firstName || '', // Optional first name
      family_name: formData.lastName || '',  // Optional last name
      username: formData.username,           // User-provided username
      'custom:accountType': formData.accountType, // Pass the account type to Cognito
    });

    if (result.success) {
      setMessage('Sign up successful! Please check your email for verification code.');
      setMessageType('success');
      setStep('confirmSignUp');
      setSignUpUsername(result.username); // Store the generated username
    } else {
      setMessage(result.error || 'Sign up failed');
      setMessageType('error');
    }
    setLoading2(false);
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();

    if (!formData.confirmationCode) {
      setErrors({ confirmationCode: 'Verification code is required' });
      return;
    }

    setLoading2(true);
    // Use the stored username for verification, not the email
    const result = await confirmSignUp(signUpUsername, formData.confirmationCode);

    if (result.success) {
      setMessage('Email verified! You can now sign in.');
      setMessageType('success');

      // After successful verification, switch to sign-in tab
      setTimeout(() => {
        setCurrentTab(0); // Switch to sign in tab
        setStep('initial');
        setMessage(''); // Clear the message
      }, 2000);
    } else {
      setMessage(result.error || 'Verification failed');
      setMessageType('error');
    }
    setLoading2(false);
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setErrors({ email: 'Please enter your email first' });
      return;
    }

    setLoading2(true);
    const result = await resetPassword(formData.email);

    if (result.success) {
      setMessage('Password reset code sent to your email.');
      setMessageType('success');
      setStep('confirmReset');
    } else {
      setMessage(result.error || 'Password reset failed');
      setMessageType('error');
    }
    setLoading2(false);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();

    if (!formData.confirmationCode || !formData.password) {
      setErrors({
        confirmationCode: !formData.confirmationCode ? 'Verification code is required' : '',
        password: !formData.password ? 'New password is required' : ''
      });
      return;
    }

    setLoading2(true);
    const result = await confirmResetPassword(formData.email, formData.confirmationCode, formData.password);

    if (result.success) {
      setMessage('Password reset successful! You can now sign in.');
      setMessageType('success');
      setCurrentTab(0);
      setStep('initial');
    } else {
      setMessage(result.error || 'Password reset confirmation failed');
      setMessageType('error');
    }
    setLoading2(false);
  };

  const renderSignInForm = () => (
    <Box component="form" onSubmit={handleSignIn} sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="Email or Username"
        value={formData.email}
        onChange={handleInputChange('email')}
        error={!!errors.email}
        helperText={errors.email || 'Use your email or chosen username'}
        margin="normal"
        required
        autoComplete="username"
      />

      <TextField
        fullWidth
        label="Password"
        type="password"
        value={formData.password}
        onChange={handleInputChange('password')}
        error={!!errors.password}
        helperText={errors.password}
        margin="normal"
        required
        autoComplete="current-password"
      />

      <Box sx={{ mt: 2, mb: 2 }}>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading2}
          sx={{ mb: 2 }}
        >
          {loading2 ? <CircularProgress size={24} /> : 'Sign In'}
        </Button>

        <Link
          component="button"
          type="button"
          onClick={handleForgotPassword}
          sx={{ display: 'block', textAlign: 'center' }}
        >
          Forgot your password?
        </Link>
      </Box>
    </Box>
  );

  const renderSignUpForm = () => (
    <Box component="form" onSubmit={handleSignUp} sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="Username"
        value={formData.username}
        onChange={handleInputChange('username')}
        error={!!errors.username}
        helperText={errors.username || 'Choose a unique username (letters, numbers, and underscores only)'}
        margin="normal"
        required
        autoComplete="username"
      />

      <FormControl fullWidth margin="normal">
        <InputLabel id="account-type-label">Account Type</InputLabel>
        <Select
          labelId="account-type-label"
          value={formData.accountType}
          label="Account Type"
          onChange={handleInputChange('accountType')}
        >
          <MenuItem value="distributor">Distributor (Photo Creator/Seller)</MenuItem>
          <MenuItem value="uploader">Uploader (Helper for Distributors)</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          label="First Name (Optional)"
          value={formData.firstName}
          onChange={handleInputChange('firstName')}
          error={!!errors.firstName}
          helperText={errors.firstName}
          margin="normal"
          autoComplete="given-name"
        />

        <TextField
          fullWidth
          label="Last Name (Optional)"
          value={formData.lastName}
          onChange={handleInputChange('lastName')}
          error={!!errors.lastName}
          helperText={errors.lastName}
          margin="normal"
          autoComplete="family-name"
        />
      </Box>

      <TextField
        fullWidth
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleInputChange('email')}
        error={!!errors.email}
        helperText={errors.email}
        margin="normal"
        required
        autoComplete="email"
      />

      <TextField
        fullWidth
        label="Password"
        type="password"
        value={formData.password}
        onChange={handleInputChange('password')}
        error={!!errors.password}
        helperText={errors.password}
        margin="normal"
        required
        autoComplete="new-password"
      />

      <TextField
        fullWidth
        label="Confirm Password"
        type="password"
        value={formData.confirmPassword}
        onChange={handleInputChange('confirmPassword')}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        margin="normal"
        required
        autoComplete="new-password"
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading2}
        sx={{ mt: 2 }}
      >
        {loading2 ? <CircularProgress size={24} /> : 'Sign Up'}
      </Button>
    </Box>
  );

  const renderConfirmSignUpForm = () => (
    <Box component="form" onSubmit={handleConfirmSignUp} sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Please enter the verification code sent to {formData.email}
      </Typography>

      <TextField
        fullWidth
        label="Verification Code"
        value={formData.confirmationCode}
        onChange={handleInputChange('confirmationCode')}
        error={!!errors.confirmationCode}
        helperText={errors.confirmationCode}
        margin="normal"
        required
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading2}
        sx={{ mt: 2 }}
      >
        {loading2 ? <CircularProgress size={24} /> : 'Verify Email'}
      </Button>
    </Box>
  );

  const renderConfirmResetForm = () => (
    <Box component="form" onSubmit={handleConfirmReset} sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Please enter the verification code sent to {formData.email} and your new password
      </Typography>

      <TextField
        fullWidth
        label="Verification Code"
        value={formData.confirmationCode}
        onChange={handleInputChange('confirmationCode')}
        error={!!errors.confirmationCode}
        helperText={errors.confirmationCode}
        margin="normal"
        required
      />

      <TextField
        fullWidth
        label="New Password"
        type="password"
        value={formData.password}
        onChange={handleInputChange('password')}
        error={!!errors.password}
        helperText={errors.password}
        margin="normal"
        required
        autoComplete="new-password"
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading2}
        sx={{ mt: 2 }}
      >
        {loading2 ? <CircularProgress size={24} /> : 'Reset Password'}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#2997ff" }}>
      {/* Header with Logo */}
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 8 } }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              src={HeaderWhite}
              alt="ShieldShot"
              sx={{ height: "2.2rem", mr: 1 }}
            />
            {process.env.NODE_ENV !== "production" && (
              <Chip
                label="Beta"
                color="primary"
                size="small"
                sx={{
                  ml: 1,
                  background: "white",
                  color: "#2997ff",
                  fontWeight: 700,
                }}
              />
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Login Form */}
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'white',
            borderRadius: 2,
            p: 4,
            boxShadow: 3,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome to ShieldShot
          </Typography>

          {message && (
            <Alert severity={messageType} sx={{ width: '100%', mb: 2 }}>
              {message}
            </Alert>
          )}

          {step === 'confirmSignUp' ? (
            <Box sx={{ width: '100%' }}>
              <Typography variant="h5" component="h2" gutterBottom align="center">
                Verify Your Email
              </Typography>
              {renderConfirmSignUpForm()}
            </Box>
          ) : step === 'confirmReset' ? (
            <Box sx={{ width: '100%' }}>
              <Typography variant="h5" component="h2" gutterBottom align="center">
                Reset Password
              </Typography>
              {renderConfirmResetForm()}
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                centered
                sx={{ mb: 2 }}
              >
                <Tab label="Sign In" />
                <Tab label="Sign Up" />
              </Tabs>

              {currentTab === 0 ? renderSignInForm() : renderSignUpForm()}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default CognitoLoginForm;
